/**
 * FotosAntesModal - Modal para Capturar Fotos PREVIO del Mantenimiento
 * 
 * Modal específico para que los mecánicos documenten el estado inicial
 * del vehículo antes de comenzar el trabajo.
 */

import React, { useState, useEffect } from 'react';
import { FaCamera, FaSpinner, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import Modal from '@/components/common/Modal';
import { ImageUploadManager } from '@/components/images';
import ImageGalleryWithViewer from '@/components/images/ImageGalleryWithViewer';
import CitaImagenesApiService from '@/services/citaImagenesApiService';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { useSucursal } from '@/context/SucursalContext';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-hot-toast';
import PatenteDisplay from '@/components/common/PatenteDisplay';

const FotosAntesModal = ({ 
  isOpen, 
  onClose, 
  cita, 
  onSuccess 
}) => {
  const [imagenes, setImagenes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imagenesExistentes, setImagenesExistentes] = useState([]);
  const [loading, setLoading] = useState(false);

  const { empresaActual } = useSimpleEmpresa();
  const { sucursalData } = useSucursal();
  const { session } = useSession();

  // Cargar imágenes existentes cuando se abre el modal
  useEffect(() => {
    if (isOpen && cita?.id) {
      cargarImagenesExistentes();
    }
  }, [isOpen, cita?.id]);

  const cargarImagenesExistentes = async () => {
    if (!cita?.id) return;
    
    setLoading(true);
    try {
      const imagenesExist = await CitaImagenesApiService.obtenerImagenesCita(session, cita.id);
      setImagenesExistentes(imagenesExist);
      
      if (imagenesExist.length > 0) {
        console.log(`Cargadas ${imagenesExist.length} imágenes existentes para cita ${cita.id}`);
      }
    } catch (error) {
      console.error('Error cargando imágenes existentes:', error);
      // No mostrar error toast para no interferir con la UX
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en ImageUploadManager
  const handleImagenesChange = (nuevasImagenes) => {
    setImagenes(nuevasImagenes);
  };

  // Eliminar imagen existente
  const eliminarImagenExistente = async (imagenId) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta imagen?')) {
      return;
    }

    try {
      await CitaImagenesApiService.eliminarImagen(session, cita.id, imagenId);
      
      // Actualizar la lista de imágenes existentes
      setImagenesExistentes(prev => prev.filter(img => img.id !== imagenId));
      
      toast.success('Imagen eliminada correctamente');
    } catch (error) {
      console.error('Error eliminando imagen:', error);
      toast.error('Error al eliminar la imagen');
    }
  };

  // Guardar todas las imágenes
  const handleGuardar = async () => {
    // Prevenir múltiples llamadas simultáneas
    if (uploading) {
      console.log('⚠️ Upload ya en progreso, ignorando click adicional');
      return;
    }

    if (imagenes.length === 0 && imagenesExistentes.length === 0) {
      toast.error('Debe tomar al menos una foto del estado inicial');
      return;
    }

    // Si no hay imágenes nuevas, cerrar directamente
    if (imagenes.length === 0) {
      onSuccess?.();
      onClose();
      return;
    }

    console.log('🖼️ [FotosAntesModal] Iniciando proceso de guardado:', {
      totalImagenes: imagenes.length,
      imagenesExistentes: imagenesExistentes.length,
      imagenesData: imagenes.map((img, index) => ({
        index,
        fileName: img.originalFile?.name,
        descripcion: img.descripcion,
        hasCompressedFile: !!img.compressedFile,
        hasOriginalFile: !!img.originalFile
      }))
    });

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const totalImagenes = imagenes.length;
      const imagenesSubidas = [];
      
      for (let i = 0; i < imagenes.length; i++) {
        const imagen = imagenes[i];
        
        console.log(`🖼️ [FotosAntesModal] Subiendo imagen ${i + 1}/${totalImagenes}:`, {
          fileName: imagen.originalFile?.name,
          uniqueFileName: imagen.uniqueFileName,
          descripcion: imagen.descripcion,
          hasCompressedFile: !!imagen.compressedFile,
          orden: imagenesExistentes.length + i
        });
        
        try {
          // Actualizar progreso
          setUploadProgress(Math.round(((i) / totalImagenes) * 100));
          
          // Verificar que el archivo comprimido existe
          if (!imagen.compressedFile) {
            throw new Error('Archivo comprimido no disponible');
          }
          
          // Subir imagen usando el API service
          const imagenSubida = await CitaImagenesApiService.subirImagen(
            session,
            cita.id,
            empresaActual?.id,
            sucursalData?.id,
            imagen.compressedFile,
            imagen.descripcion,
            imagenesExistentes.length + i, // Orden continua desde existentes
            imagen.uniqueFileName // Usar nombre único generado
          );
          
          console.log(`✅ [FotosAntesModal] Imagen ${i + 1} subida exitosamente:`, imagenSubida.id);
          imagenesSubidas.push(imagenSubida);
          
        } catch (error) {
          console.error(`❌ [FotosAntesModal] Error subiendo imagen ${i + 1}:`, error);
          console.error('Detalles de la imagen que falló:', {
            fileName: imagen.originalFile?.name,
            hasOriginalFile: !!imagen.originalFile,
            hasCompressedFile: !!imagen.compressedFile,
            descripcion: imagen.descripcion
          });
          toast.error(`Error subiendo imagen: ${imagen.originalFile?.name || 'Archivo desconocido'}`);
          // No hacer return aquí, continuar con las siguientes imágenes
        }
      }
      
      setUploadProgress(100);
      
      console.log('🖼️ [FotosAntesModal] Proceso de guardado completado:', {
        totalIntentadas: totalImagenes,
        exitosas: imagenesSubidas.length,
        fallos: totalImagenes - imagenesSubidas.length,
        imagenesSubidas: imagenesSubidas.map(img => ({ id: img.id, nombre: img.nombre_archivo }))
      });
      
      if (imagenesSubidas.length > 0) {
        const mensaje = imagenesSubidas.length === totalImagenes 
          ? `${imagenesSubidas.length} imagen${imagenesSubidas.length > 1 ? 'es' : ''} guardada${imagenesSubidas.length > 1 ? 's' : ''} correctamente`
          : `${imagenesSubidas.length} de ${totalImagenes} imágenes guardadas correctamente`;
        
        toast.success(mensaje);
        
        // Callback de éxito
        if (onSuccess) {
          onSuccess(imagenesSubidas);
        }
        
        // Limpiar estado y cerrar
        setImagenes([]);
        onClose();
      } else {
        console.error('❌ [FotosAntesModal] No se pudo guardar ninguna imagen');
        toast.error('No se pudo guardar ninguna imagen');
      }
      
    } catch (error) {
      console.error('Error en handleGuardar:', error);
      toast.error('Error al guardar las imágenes');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Limpiar estado al cerrar
  const handleClose = () => {
    if (uploading) {
      const confirmar = window.confirm('¿Está seguro de cancelar? Se perderán las imágenes sin guardar.');
      if (!confirmar) return;
    }
    
    setImagenes([]);
    setUploadProgress(0);
    setUploading(false);
    onClose();
  };

  const modalTitle = (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <span className="text-white font-medium">Fotos Estado Previo</span>
      </div>
      {cita && (
        <div className="flex items-center">
          <PatenteDisplay patente={cita.patente} size="sm" />
        </div>
      )}
    </div>
  );

  const modalFooter = (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
      {/* Información del progreso */}
      <div className="text-sm text-gray-400 order-2 sm:order-1">
        {uploading ? (
          <div className="flex items-center gap-2">
            <FaSpinner className="animate-spin" />
            <span>Subiendo... {uploadProgress}%</span>
          </div>
        ) : (
          <span>
            {imagenes.length + imagenesExistentes.length} imagen{(imagenes.length + imagenesExistentes.length) !== 1 ? 'es' : ''} total
          </span>
        )}
      </div>
      
      {/* Botones */}
      <div className="flex gap-3 order-1 sm:order-2">
        <button
          type="button"
          onClick={handleClose}
          disabled={uploading}
          className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancelar
        </button>
        
        <button
          type="button"
          onClick={handleGuardar}
          disabled={uploading || (imagenes.length === 0 && imagenesExistentes.length === 0)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <>
              <FaSpinner className="animate-spin mr-2" size={14} />
              Guardando...
            </>
          ) : imagenes.length > 0 ? (
            <>
              <FaCamera className="mr-2" size={14} />
              Guardar Fotos
            </>
          ) : (
            <>
              <FaCheckCircle className="mr-2" size={14} />
              Continuar
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      size="2xl"
      footer={modalFooter}
    >
      <div className="space-y-6">
        {/* Instrucciones */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <h4 className="text-blue-300 font-medium mb-2 flex items-center">
            <FaCamera className="mr-2" />
            Instrucciones Importantes
          </h4>
          <ul className="text-sm text-blue-100 space-y-1">
            <li>• Capture fotos claras del estado actual del vehículo</li>
            <li>• Incluya todas las áreas que serán trabajadas</li>
            <li>• Tome fotos desde múltiples ángulos si es necesario</li>
            <li>• Estas fotos protegen tanto al taller como al cliente</li>
          </ul>
        </div>

        {/* Galería de imágenes existentes */}
        <ImageGalleryWithViewer
          images={imagenesExistentes}
          title="Fotos Ya Capturadas"
          onDeleteImage={eliminarImagenExistente}
          disabled={uploading}
          viewerTitle={`Fotos Estado Previo - ${cita?.patente || 'Vehículo'}`}
        />

        {/* Componente de upload de imágenes */}
        {loading ? (
          <div className="text-center py-8">
            <FaSpinner className="animate-spin mx-auto text-blue-400 mb-3" size={24} />
            <p className="text-gray-400">Cargando imágenes existentes...</p>
          </div>
        ) : (
          <ImageUploadManager
            tipo="antes"
            maxImages={4 - imagenesExistentes.length} // Considerar imágenes existentes
            required={imagenesExistentes.length === 0} // Solo requerido si no hay existentes
            onImagesChange={handleImagenesChange}
            citaId={cita?.id}
            empresaId={empresaActual?.id}
            sucursalId={sucursalData?.id}
            disabled={uploading}
          />
        )}

        {/* Advertencia si no puede tomar más fotos */}
        {imagenesExistentes.length >= 4 && (
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-center text-yellow-300">
              <FaExclamationTriangle className="mr-2" />
              <span className="text-sm">
                Límite de imágenes alcanzado. Ya tiene {imagenesExistentes.length} fotos del estado inicial.
              </span>
            </div>
          </div>
        )}

        {/* Barra de progreso durante subida */}
        {uploading && (
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-white">Subiendo imágenes...</span>
              <span className="text-sm text-gray-400">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FotosAntesModal;